import { useRef, useState, type KeyboardEvent } from "react";
import { ChevronRight, X } from "lucide-react";
import { cn } from "@/utils/helpers";
import type { MarketSignal } from "@/types/marketSignal.type";
import { scrubVendorTerms } from "@/lib/vendorTerms";
import { citationHeadline, citationOf } from "./evidenceDisplay";
import { formatShortDate } from "./signalMeta";
import SignalsDialog from "./SignalsDialog";
import SignalSourceDetail from "./SignalSourceDetail";
import SourceFavicon from "./SourceFavicon";

/**
 * Every source behind one signal: the list on the left, the selected source
 * read in full on the right (a bottom sheet with the list above the detail on
 * a phone).
 *
 * The list is a vertical tablist with one tab stop: the arrow keys (and Home /
 * End) move through it and select as they go, and the detail is its tabpanel.
 * It opens on `initialId` when a particular source was clicked, and focus lands
 * on that source's row. The parent mounts this only while it is open.
 *
 * With `onPickSource` (a phone) it is the list alone, and a source opens in the
 * source sheet instead.
 */
export default function SignalSourcesModal({
  signal,
  initialId,
  onClose,
  onPickSource,
}: {
  signal: MarketSignal;
  /** Open on this source (the row that was clicked) rather than the first. */
  initialId?: string | null;
  onClose: () => void;
  /** Phone: open the source at this index in the source sheet. */
  onPickSource?: (index: number) => void;
}) {
  const observations = signal.groundingObservations ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(
    () => (initialId && observations.some((o) => o.id === initialId) ? initialId : null) ??
      observations[0]?.id ??
      null,
  );
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const detailRef = useRef<HTMLDivElement>(null);

  const found = observations.findIndex((o) => o.id === selectedId);
  const selectedIndex = found >= 0 ? found : 0;
  const selected = observations[selectedIndex] ?? null;

  const selectAt = (index: number, focus: boolean) => {
    const next = Math.max(0, Math.min(observations.length - 1, index));
    const o = observations[next];
    if (!o) return;
    setSelectedId(o.id);
    detailRef.current?.scrollTo({ top: 0 });
    if (focus) {
      const el = itemRefs.current[next];
      el?.focus();
      el?.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  };

  const onItemKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight":
        e.preventDefault();
        selectAt(index + 1, true);
        break;
      case "ArrowUp":
      case "ArrowLeft":
        e.preventDefault();
        selectAt(index - 1, true);
        break;
      case "Home":
        e.preventDefault();
        selectAt(0, true);
        break;
      case "End":
        e.preventDefault();
        selectAt(observations.length - 1, true);
        break;
      default:
        break;
    }
  };

  const n = observations.length;

  // Signals / Phone / Sources sheet: the list alone, each source a card that
  // opens it in the source sheet.
  if (onPickSource) {
    return (
      <SignalsDialog labelledBy="sig-sources-title" onClose={onClose} className="sig-dialog__panel--srclist">
        <div className="sig-srcl">
          <span className="sig-srcl__grab" aria-hidden="true" />
          <div className="sig-srcl__row">
            <h2 className="sig-srcl__heading" id="sig-sources-title">
              Sources
            </h2>
            <span className="sig-srcl__count">{n}</span>
            <button type="button" className="sig-icon-btn sig-icon-btn--lg sig-srcl__close" aria-label="Close" onClick={onClose}>
              <X aria-hidden="true" />
            </button>
          </div>
          <p className="sig-srcl__signal" dir="auto">
            {signal.title}
          </p>
          <ul className="sig-srcl__list">
            {observations.map((o, i) => {
              const c = citationOf(o);
              return (
                <li key={o.id}>
                  <button type="button" className="sig-srcl__item" onClick={() => onPickSource(i)}>
                    <span className="sig-srcl__text">
                      <span className="sig-srcl__src">
                        <SourceFavicon host={c.host} size={13} />
                        <span className="sig-srcl__host">{c.host ?? "Source"}</span>
                        <span className="sig-srcl__date">· {formatShortDate(c.observedAt)}</span>
                      </span>
                      <span className="sig-srcl__title" dir="auto">
                        {scrubVendorTerms(citationHeadline(c))}
                      </span>
                    </span>
                    <ChevronRight aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </SignalsDialog>
    );
  }

  return (
    <SignalsDialog
      labelledBy="sig-sources-title"
      onClose={onClose}
      className="sig-dialog__panel--sources"
    >
      <div className="sig-src">
        <div className="sig-src__list">
          <div className="sig-src__head">
            <h2 className="sig-src__heading" id="sig-sources-title">
              Sources <span className="sig-src__count">{n}</span>
            </h2>
            <p className="sig-src__signal" dir="auto">
              {signal.title}
            </p>
          </div>

          <div
            className="sig-src__items"
            role="tablist"
            aria-orientation="vertical"
            aria-label="Sources"
          >
            {observations.map((o, i) => {
              const c = citationOf(o);
              const on = i === selectedIndex;
              return (
                <button
                  key={o.id}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`sig-src-tab-${i}`}
                  aria-selected={on}
                  aria-controls="sig-src-detail"
                  tabIndex={on ? 0 : -1}
                  className={cn("sig-src__item", on && "is-on")}
                  onClick={() => selectAt(i, false)}
                  onKeyDown={(e) => onItemKeyDown(e, i)}
                >
                  <span className="sig-src__item-src">
                    <SourceFavicon host={c.host} />
                    <span className="sig-src__item-host">{c.host ?? "Source"}</span>
                    <span className="sig-src__item-date">{formatShortDate(c.observedAt)}</span>
                  </span>
                  <span className="sig-src__item-title" dir="auto">
                    {scrubVendorTerms(citationHeadline(c))}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div
          className="sig-src__detail"
          id="sig-src-detail"
          role="tabpanel"
          aria-labelledby={`sig-src-tab-${selectedIndex}`}
          ref={detailRef}
        >
          <button type="button" className="sig-icon-btn sig-src__close" aria-label="Close" onClick={onClose}>
            <X aria-hidden="true" />
          </button>
          {selected ? <SignalSourceDetail key={selected.id} observation={selected} /> : null}
        </div>
      </div>
    </SignalsDialog>
  );
}
